import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import labelScanReviewService, { QueueSummary, ReviewQueueRow } from '../../../services/labelScanReviewService';

/**
 * P5c — the operator's scan review queue (design §7). Cross-venue and oldest
 * first: the operator curates the shared identity layer, so the queue is the
 * whole estate's pending scans, worked from the back.
 */

const STATUS_COLOR: Record<string, 'warning' | 'info' | 'error'> = {
  proposed: 'info',
  matched_producer: 'warning',
  flagged: 'error',
};

const STATUS_FILTERS = ['all', 'proposed', 'matched_producer', 'flagged'] as const;

/**
 * §5a — the operational banner. What the daily email used to say, said
 * where the operator already is: how deep the queue is, how long the
 * oldest has waited, what was decided and taught, what it cost — and,
 * loudest, anything a source contradicts.
 */
const SummaryBanner: React.FC<{ summary: QueueSummary }> = ({ summary }) => {
  const alarm = summary.contradictions.length > 0;
  const statusLine = Object.entries(summary.pending.byStatus)
    .map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(' · ') || 'none';
  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      {alarm && (
        <Alert severity="error" variant="filled">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {summary.contradictions.length} scan{summary.contradictions.length === 1 ? '' : 's'} where a source contradicts the label — a person must adjudicate.
          </Typography>
          {summary.contradictions.map((c) => (
            <Typography key={c.scanId} variant="caption" display="block">
              · {c.producerText ?? 'unreadable'}
              {c.matchedProbe && ' — MATCHED-PROBE: suspected precision failure'}
            </Typography>
          ))}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="baseline">
          <Box>
            <Typography variant="h6">{summary.pending.total}</Typography>
            <Typography variant="caption" color="text.secondary">pending · {statusLine}</Typography>
          </Box>
          {summary.pending.oldestDays !== null && (
            <Box>
              <Typography variant="h6" color={summary.pending.oldestDays >= 7 ? 'warning.main' : 'inherit'}>
                {summary.pending.oldestDays}d
              </Typography>
              <Typography variant="caption" color="text.secondary">oldest waiting</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="h6">
              {Object.values(summary.decided).reduce((a, b) => a + b, 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              decided in 24h{Object.keys(summary.decided).length
                ? ` · ${Object.entries(summary.decided).map(([k, v]) => `${v} ${k}`).join(' · ')}` : ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6">{summary.taughtAliases}</Typography>
            <Typography variant="caption" color={summary.refusedAliases ? 'warning.main' : 'text.secondary'}>
              aliases taught{summary.refusedAliases ? ` · ${summary.refusedAliases} refused (collisions)` : ''}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6">${summary.spendUsd.toFixed(2)}</Typography>
            <Typography variant="caption" color="text.secondary">scan spend, 24h</Typography>
          </Box>
          {/* GK-1 §4.1 — grape findings are counted here and worked elsewhere:
              a coverage gap is not an inbox item, and a contradiction between
              a label and an appellation rule is not a scan to review. */}
          {summary.grape && (
            <Box>
              <Typography variant="h6" color={summary.grape.contradictions ? 'error.main' : 'inherit'}>
                {summary.grape.contradictions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                grape contradictions ·{' '}
                <Link component={RouterLink} to="/admin/governance/grape-findings" variant="caption">
                  {summary.grape.coverage} coverage gaps
                </Link>
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};

const LabelScanReviewQueue: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReviewQueueRow[]>([]);
  const [summary, setSummary] = useState<QueueSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notEnabled, setNotEnabled] = useState(false);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const contradictionIds = useMemo(
    () => new Set((summary?.contradictions ?? []).map((c) => c.scanId)),
    [summary],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await labelScanReviewService.getQueue(
        statusFilter === 'all' ? undefined : { status: statusFilter },
      );
      setRows(res.data);
      setSummary(res.summary ?? null);
      setTotal(res.total);
      setNotEnabled(false);
    } catch (e: any) {
      // The server ships the console dark behind OPERATOR_CONSOLE_ENABLED —
      // a plain 404 here means "not enabled on this environment", not a bug.
      if (e?.response?.status === 404) setNotEnabled(true);
      else setError(e?.response?.data?.message || e?.message || 'Failed to load the review queue.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  if (notEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">The operator console is not enabled on this environment.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Label Scan Review</Typography>
          <Typography variant="body2" color="text.secondary">
            Scans awaiting an operator decision, all venues, oldest first{total ? ` — ${total} pending` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {STATUS_FILTERS.map((s) => (
            <Chip
              key={s}
              label={s === 'all' ? 'All' : s.replace('_', ' ')}
              color={s !== 'all' && statusFilter === s ? STATUS_COLOR[s] : 'default'}
              variant={statusFilter === s ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter(s)}
              size="small"
            />
          ))}
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && <SummaryBanner summary={summary} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Received</TableCell>
              <TableCell>Producer (as read)</TableCell>
              <TableCell>Appellation</TableCell>
              <TableCell>Vintage</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Candidates</TableCell>
              <TableCell align="center">Flags</TableCell>
              <TableCell>Verification</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">The queue is empty.</Typography>
                </TableCell>
              </TableRow>
            ) : [...rows]
              // Contradictions pin to the top: the queue is oldest-first,
              // but "a source disagrees with the label" outranks age.
              .sort((a, b) => Number(contradictionIds.has(b._id)) - Number(contradictionIds.has(a._id)))
              .map((row) => (
              <TableRow
                key={row._id}
                hover
                sx={{
                  cursor: 'pointer',
                  ...(contradictionIds.has(row._id) && {
                    bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                    borderLeft: '3px solid',
                    borderLeftColor: 'error.main',
                  }),
                }}
                onClick={() => navigate(`/admin/governance/label-scans/${row._id}`)}
              >
                <TableCell sx={{ width: 56, p: 0.5 }}>
                  {row.thumbUrl && (
                    <Box component="img" src={row.thumbUrl} alt=""
                      sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 0.5 }} />
                  )}
                </TableCell>
                <TableCell>
                  {new Date(row.createdAt).toLocaleString()}
                  {row.claimedBy && (
                    <Chip size="small" color="warning" variant="outlined" label={`reviewing: ${row.claimedBy}`} sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>{row.producerText ?? <Typography component="span" variant="caption" color="text.secondary">—</Typography>}</TableCell>
                <TableCell>{row.appellationText ?? '—'}</TableCell>
                <TableCell>{row.vintage ?? '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.status.replace('_', ' ')} color={STATUS_COLOR[row.status] ?? 'default'} />
                </TableCell>
                <TableCell align="center">{row.candidates}</TableCell>
                <TableCell align="center">{row.flags || '—'}</TableCell>
                <TableCell>
                  {row.verification
                    ? <Chip size="small" variant="outlined" label={row.verification} />
                    : <Typography component="span" variant="caption" color="text.secondary">none</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LabelScanReviewQueue;
